import React, { useEffect, useState } from 'react';
import { Box, Typography, Grid, Paper, Tooltip, IconButton } from '@mui/material';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import duration from 'dayjs/plugin/duration';
import AttachmentIcon from '@mui/icons-material/Attachment';

import { AttachmentDialogUI, CheckboxUI, ConfirmDialogUI, LinearStaticProgressUI } from '../../Interface';
import productionService from '../../../service/production.service';
import OpWidgetReasonDialog from './OpWidgetReasonDialog';
import { ColorPicker } from '../../../config/ColorPicker';
import OpWidgetMultiManDialog from './OpWidgetMultiManDialog';
import OpWidgetQuantityDialog from './OpWidgetQuantityDialog';
import OpWidgetGrouppedQuantityDialog from './OpWidgetGrouppedQuantityDialog';

dayjs.extend(relativeTime);
dayjs.extend(duration);

const OpWidgetJobmonitorCard2 = ({
  data,
  userId,
  setRefetch,
  checkedIds,
  handleCheckboxChange,
  rows,
  params,
  helperData,
  reasons,
  useGrouppedState,
  setGrouppedOpDialog,
  autoCompleteOptions,
  feedBackDialogDataState,
}) => {
  const { useGroupped } = useGrouppedState;
  const { setFeedbackDialogData } = feedBackDialogDataState;

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmDialogData, setConfirmDialogData] = useState(null);
  const [isInterruptionDialogOpen, setIsInterruptionDialogOpen] = useState(false);
  const [isDowntimeDialogOpen, setIsDowntimeDialogOpen] = useState(false);
  const [isEmployeeGroupDialogOpen, setIsEmployeeDialogOpen] = useState(false);
  const [isQuantityDialogOpen, setIsQuantityDialogOpen] = useState(false);
  const [isGrouppedDialogOpen, setIsGrouppedDialogOpen] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(null);
  const [timer, setTimer] = useState(null);
  const [employeeGroups, setEmployeeGroups] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectedReasonCode, setSelectedReasonCode] = useState(null);
  const [isAttachmentDialogOpen, setIsAttachmentDialogOpen] = useState(false);
  const [clicked, setClicked] = useState(false);

  const [attachments, setAttachments] = useState([]);

  const startedOperation = data?.productionReport?.find(
    (report) => report.startDate && !report.endDate && report.userId === userId
  );

  const operation = rows?.find((op) => op.id === data.id);

  const groupedOperations =
    operation?.groupReportingId != null ? rows.filter((op) => op.groupReportingId === operation.groupReportingId) : [];

  const isGrouped = groupedOperations.length > 1;

  const lastUserReport = data.productionReport?.[data.productionReport.length - 1];
  const shouldShowInterruptReason = lastUserReport?.userId === userId && !!lastUserReport?.interruptionCode;

  useEffect(() => {
    if (startedOperation?.startDate) {
      if (timer) {
        clearInterval(timer);
      }

      const interval = setInterval(() => {
        const start = dayjs(startedOperation.startDate);
        const now = dayjs();
        const diff = dayjs.duration(now.diff(start));

        setElapsedTime(
          `${diff.hours().toString().padStart(2, '0')}:${diff.minutes().toString().padStart(2, '0')}:${diff.seconds().toString().padStart(2, '0')}`
        );
      }, 1000);

      setTimer(interval);

      return () => clearInterval(interval);
    } else {
      if (timer) {
        clearInterval(timer);
        setTimer(null);
      }
      setElapsedTime(null);
    }
  }, [startedOperation]);

  const { totalReportedQty, totalReportedTimeSpent, totalReportedTimeSpentInMin } = data.productionReport.reduce(
    (acc, report) => {
      if (report.prodOperationId === data.id) {
        acc.totalReportedQty += Number(report.quantity);
        acc.totalReportedTimeSpent += Number(report.timeSpent);
        acc.totalReportedTimeSpentInMin += Number(report.timeSpentInMin);
      }
      return acc;
    },
    { totalReportedQty: 0, totalReportedTimeSpent: 0, totalReportedTimeSpentInMin: 0 }
  );

  const reported =
    params?.QUANTITY_UNIT_HOUR_CODE === data.quantityUnit.code ? totalReportedTimeSpent : totalReportedTimeSpentInMin;

  const preparedness = reported ? Math.round((reported / data.plannedQty) * 100) : 0;

  const handleUpdateStatus = (productionStatusId, ids) => {
    return productionService.updateStatus({
      id: ids && ids.length > 0 ? ids : data.id,
      productionStatusId,
      updatedBy: userId,
    });
  };

  const handleStartOperation = () => {
    if (clicked) return;
    setClicked(true);

    let operationIds = isGrouped ? groupedOperations.map((op) => op.id) : [data.id];

    const values = {
      userId: userId,
      createdBy: userId,
      id: checkedIds.length > 0 ? checkedIds : operationIds.length > 1 ? operationIds : data.id,
      employees: selectedEmployees,
    };

    const startOperation = productionService.createProductionReport(values);
    Promise.all([
      handleUpdateStatus(
        'INPROGRESS',
        checkedIds.length > 0 ? checkedIds : operationIds.length > 1 ? operationIds : data.id
      ),
      startOperation,
    ])
      .then(() => {
        setRefetch(true);
        setSelectedEmployees([]);
        setIsEmployeeDialogOpen(false);
        setShowConfirmDialog(false);
      })
      .finally(() => {
        setClicked(false);
      });
  };

  const handleStopOperation = (interruptionCode = null, downtimeCode = null, data = {}, isContinuation = false) => {
    if (clicked) return;
    setClicked(true);
    const operation = rows?.find((row) => row.id === startedOperation.prodOperationId);

    let productionReportIds = [];

    if (operation?.groupReportingId) {
      const groupedOperations = rows.filter((row) => row.groupReportingId === operation.groupReportingId);

      productionReportIds = groupedOperations
        .flatMap((op) =>
          (op.productionReport || []).filter(
            (report) => report.startDate && !report.endDate && report.userId === userId
          )
        )
        .map((report) => report.id);
    }

    const values = {
      interruptionCode: interruptionCode || (selectedReasonCode?.trim() ? selectedReasonCode : null),
      downtimeCode,
      userId,
      quantities: data,
      createNew: downtimeCode || isContinuation,
      id: productionReportIds && productionReportIds.length > 0 ? productionReportIds : startedOperation.id,
    };
    return productionService
      .updateProductionReport(values)
      .then(() => {
        setIsDowntimeDialogOpen(false);
        setShowConfirmDialog(false);
        setIsQuantityDialogOpen(false);
        setSelectedReasonCode('');
        setIsGrouppedDialogOpen(false);
        setRefetch(true);
      })
      .finally(() => {
        setClicked(false);
      });
  };

  const handleFinishOperation = (values) => {
    let operationIds = isGrouped ? groupedOperations.map((op) => op.id) : [data.id];

    Promise.all([
      handleUpdateStatus(
        'COMPLETED',
        checkedIds?.length > 0 ? checkedIds : operationIds && operationIds?.length > 0 ? operationIds : data.id
      ),
      handleStopOperation(null, null, values),
    ]).then(() => {
      setRefetch(true);
    });
  };

  const handleInterruptOperation = (interruptionCode = null, values) => {
    if (clicked) return;
    setClicked(true);

    const operation = rows?.find((op) => op.id === data.id);

    let operationIds = [data.id];

    if (operation?.groupReportingId) {
      const groupedOperations = rows?.filter((op) => op?.groupReportingId === operation?.groupReportingId);
      operationIds = groupedOperations.map((op) => op.id);
    }

    Promise.all([
      handleUpdateStatus(
        'INTERRUPTED',
        checkedIds?.length > 0 ? checkedIds : operationIds && operationIds?.length > 0 ? operationIds : data.id
      ),
      handleStopOperation(interruptionCode, null, values),
      setIsInterruptionDialogOpen(false),
      setShowConfirmDialog(false),
    ])
      .then(() => {
        setRefetch(true);
      })
      .finally(() => {
        setClicked(false);
      });
  };

  const openConfirmDialog = () => {
    const operation = rows?.find((row) => row.id === data.id);

    let groupedOperations = [];
    if (operation?.groupReportingId) {
      groupedOperations = rows.filter((row) => row.groupReportingId === operation.groupReportingId);
    }

    const isGrouped = groupedOperations.length > 1;

    if (startedOperation && !startedOperation.downtimeCode) {
      setConfirmDialogData({
        message: 'Mit szeretne csinálni?',
        confirmButtonText: 'Kész',
        joker1ButtonText: 'Megszakít',
        joker2ButtonText: 'Állásidő',
        handleChangeElement: () => {
          setShowConfirmDialog(false);
          if (isGrouped) {
            setIsGrouppedDialogOpen(true);
          } else {
            setIsQuantityDialogOpen(true);
          }
        },
        handleChangeJoker1Element: () => {
          setIsInterruptionDialogOpen(true);
        },
        handleChangeJoker2Element: () => {
          setIsDowntimeDialogOpen(true);
        },
      });
    } else if (startedOperation && startedOperation.downtimeCode) {
      setConfirmDialogData({
        message: 'Mit szeretne csinálni?',
        confirmButtonText: 'Kész',
        joker1ButtonText: 'Megszakít',
        joker2ButtonText: 'Folytatás',
        handleChangeElement: () => {
          setShowConfirmDialog(false);
          if (isGrouped) {
            setIsGrouppedDialogOpen(true);
          } else {
            setIsQuantityDialogOpen(true);
          }
        },
        handleChangeJoker1Element: () => {
          setIsInterruptionDialogOpen(true);
        },
        handleChangeJoker2Element: () => {
          handleStopOperation(null, null, {}, true);
        },
      });
    } else {
      if (useGroupped) {
        return setGrouppedOpDialog(true);
      }
      setConfirmDialogData({
        message: 'Biztosan el szeretné indítani a gyártási műveletet?',
        confirmButtonText: 'Igen',
        handleChangeElement: () => {
          handleStartClick();
        },
      });
    }
    setShowConfirmDialog(true);
  };

  const getBackgroundColor = (code) => {
    const foundedColor = helperData?.status?.find((status) => status.code === code)?.color;
    return ColorPicker.find((cl) => cl.colorName === foundedColor);
  };

  const handleStartClick = () => {
    if (data.operation?.groupReporting && data.operation?.multiManOperation > 1) {
      const findedGroup = helperData?.operationGroup?.find((group) =>
        group.operationGroupLink?.some((link) => link.operationId === data.operationId)
      );

      const usersInGroup = findedGroup?.operationGroupUserLink
        ?.filter((link) => !link.user?.groupLeader)
        .map((link) => link.user);

      setEmployeeGroups(usersInGroup);
      setIsEmployeeDialogOpen(true);
      setShowConfirmDialog(false);
    } else {
      handleStartOperation();
    }
  };

  const uniqueResources = Array.from(
    new Map(data.productionOperationMultiResource.map((item) => [item.resourceOneId, item])).values()
  );

  const handleGroupedInterruptOperation = (values) => {
    handleInterruptOperation(selectedReasonCode, values);
    setSelectedReasonCode('');
  };

  const getProdStartDate = (operationCode, doc) => {
    if (!operationCode || !doc) return null;
    const isDF = operationCode.includes('-DF');
    const isGL = operationCode.includes('-GL');

    let date = null;

    if (isDF) {
      date = doc.joker_date1 || doc.joker_date3 || doc.dueDate;
    } else if (isGL) {
      date = doc.joker_date1 || doc.dueDate;
    }

    return date ? dayjs(date).format('YYYY.MM.DD HH:mm') : null;
  };

  return (
    <Grid item xs={12}>
      <Box
        sx={{
          borderRadius: 2,
          background: '#E4F0FF',
        }}
      >
        <Grid
          container
          direction="row"
          sx={{
            justifyContent: 'center',
            alignItems: 'stretch',
          }}
          p={0.5}
          columnSpacing={1}
        >
          {/* Left Section */}
          <Grid item xs={2}>
            <Box
              sx={{
                borderRadius: 2,
                boxShadow:
                  ' rgba(6, 24, 44, 0.4) 0px 0px 0px 2px, rgba(6, 24, 44, 0.65) 0px 4px 6px -1px, rgba(255, 255, 255, 0.08) 0px 1px 0px inset;',
                height: '100%',
                padding: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                '&:hover': {
                  background: getBackgroundColor(data?.jmV2ExtendedData?.prevStatus)?.jobMonitorHoverColor,
                },
                backgroundColor: getBackgroundColor(data?.jmV2ExtendedData?.prevStatus)?.jobMonitorBgColor,
              }}
            >
              <Typography fontWeight="bold">{data.jmV2ExtendedData?.prevLabel}</Typography>
              <Typography fontWeight="bold" fontStyle="italic">
                {data.jmV2ExtendedData?.prevOperationName}
              </Typography>
              <Typography>{data.jmV2ExtendedData?.prevStatusLabel}</Typography>
            </Box>
          </Grid>

          {/* Center Section */}
          <Grid item xs={7}>
            <Box
              sx={{
                backgroundColor: getBackgroundColor(data?.productionStatus?.code)?.jobMonitorBgColor,
                borderRadius: 2,
                boxShadow:
                  ' rgba(6, 24, 44, 0.4) 0px 0px 0px 2px, rgba(6, 24, 44, 0.65) 0px 4px 6px -1px, rgba(255, 255, 255, 0.08) 0px 1px 0px inset;',
                padding: 1,
                height: '100%',
                cursor: 'pointer',
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#888',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  backgroundColor: '#555',
                },

                '&:hover': {
                  background: getBackgroundColor(data?.productionStatus?.code)?.jobMonitorHoverColor,
                },
              }}
              onClick={() => openConfirmDialog()}
            >
              <Grid container spacing={0}>
                <Grid item xs={12}>
                  {useGroupped && !data.groupReportingId && data.operation.groupReporting && (
                    <Box onClick={(event) => event.stopPropagation()}>
                      <CheckboxUI
                        label="Csoportos lejelentés"
                        sx={{ margin: 0, padding: '0 0.5rem 0 0' }}
                        checked={checkedIds.includes(data.id)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(event) => {
                          handleCheckboxChange(event.target.checked, data.id);
                        }}
                      />
                    </Box>
                  )}
                </Grid>
              </Grid>
              <Grid container spacing={0}>
                <Grid item xs={7} display="flex" alignItems="center">
                  <Box
                    backgroundColor="rgba(240, 237, 45, 0.74)"
                    p="0.2rem 0.6rem"
                    mr="0.5rem"
                    borderRadius="12px"
                    onClick={(event) => {
                      event.stopPropagation();
                      setFeedbackDialogData({
                        createdBy: userId,
                        productionId: data.productionId,
                        prodOperationId: data.id,
                        prodOperationLabel: data.label,
                      });
                    }}
                  >
                    <Typography fontWeight="bold">Fb</Typography>
                  </Box>
                  <IconButton
                    sx={{ mr: 1, p: 0 }}
                    onClick={(event) => {
                      event.stopPropagation();
                      setIsAttachmentDialogOpen(true);
                      setAttachments([...(data.sourceListAttachment ?? [])]);
                    }}
                  >
                    <AttachmentIcon
                      sx={{
                        fontSize: '2rem',
                        color: [...(data?.sourceListAttachment ?? [])].length > 0 && '#0065F8',
                      }}
                    />
                  </IconButton>
                  <Typography fontWeight="bold">
                    {`${data.production.workingNumber} - ${data.production?.reference} - ${data?.production?.finishedItemCode ?? ''} ${
                      data.operation.showMaterialItemCode && params.OP_WIDGET2_MATERIAL_ITEMCODE_START
                        ? `- ${
                            data.production.productionItem?.find((item) =>
                              item.itemCode?.startsWith(params.OP_WIDGET2_MATERIAL_ITEMCODE_START)
                            )?.itemCode ?? ''
                          }`
                        : ''
                    }`}
                  </Typography>
                </Grid>

                <Grid item xs={3}>
                  {startedOperation?.startDate && (
                    <Typography fontWeight="bold">
                      Elindítva: {dayjs(startedOperation?.startDate).format('YYYY-MM-DD HH:mm')}
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={2} textAlign="right">
                  <Typography fontWeight="bold" color="blue">
                    {elapsedTime}
                  </Typography>
                </Grid>
              </Grid>
              <Grid container spacing={0}>
                <Grid item xs={2}>
                  <Typography>{getProdStartDate(data.operation.code, data?.orderItem?.doc)}</Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography>{data.production.articleName}</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography>{data.production.articleNumber} </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography>{`${data.production?.productionQuantity} ${data.production?.quantityUnit?.code}`}</Typography>
                </Grid>
                <Grid item xs={2} textAlign="right">
                  <Typography>
                    {params?.QUANTITY_UNIT_HOUR_CODE === data.quantityUnit.code
                      ? `${Math.round(totalReportedTimeSpent * 100) / 100} / ${data.plannedQty} ${data.quantityUnit.name}`
                      : `${Math.round(totalReportedTimeSpentInMin * 100) / 100} / ${data.plannedQty} ${data.quantityUnit.name}`}
                  </Typography>
                </Grid>
              </Grid>
              <Grid container spacing={0}>
                <Grid item xs={3}>
                  <Typography>{data.production?.company?.name}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography>{data.production?.project?.projectName}</Typography>
                </Grid>
                <Grid item xs={5} textAlign="right">
                  <LinearStaticProgressUI
                    bgColor={startedOperation ? '#4ade80' : 'rgba(113, 113, 122, 0.5)'}
                    value={preparedness}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={0} mt="0.5rem">
                {data.operation.groupReporting &&
                  data.operation.multiManOperation > 1 &&
                  data?.productionOperationMultiResource?.length > 0 && (
                    <Grid item xs={1}>
                      <Tooltip
                        title={uniqueResources
                          .map(
                            (item) =>
                              autoCompleteOptions.resourceOne.find((res) => res.value === item.resourceOneId)?.label
                          )
                          .join(', ')}
                        placement="top"
                        arrow
                        slotProps={{
                          tooltip: {
                            sx: {
                              color: '#514E6A',
                              backgroundColor: '#ffff',
                              boxShadow: 'rgba(0, 0, 0, 0.35) 0px 5px 15px',
                              maxWidth: 400,
                              fontSize: '1rem',
                            },
                          },
                          arrow: {
                            sx: {
                              color: '#ffff',
                            },
                          },
                        }}
                      >
                        <Box
                          sx={{ border: '2px solid black', borderRadius: '15px', width: '30px', textAlign: 'center' }}
                        >
                          {uniqueResources.length}
                        </Box>
                      </Tooltip>
                    </Grid>
                  )}
                <Grid item xs={3}>
                  <Typography fontWeight="bold" fontStyle="italic">
                    {data.operation.operationName}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography>{data.description}</Typography>
                </Grid>
                <Grid item xs={3} textAlign="center">
                  {startedOperation?.downtimeCode && (
                    <Typography fontWeight="bold" sx={{ background: '#FF6363', borderRadius: '8px' }}>
                      Állás oka: {startedOperation?.downtimeReason?.name}
                    </Typography>
                  )}
                  {shouldShowInterruptReason && (
                    <Typography fontWeight="bold" sx={{ background: '#FF6363', borderRadius: '8px' }}>
                      Megszakítás oka: {lastUserReport.interruptionReason.name}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={3} textAlign="right">
                  <Typography>{data.productionStatus?.name}</Typography>
                </Grid>
              </Grid>
            </Box>
          </Grid>

          {/* Right Section */}
          <Grid item xs={1.5}>
            <Box
              sx={{
                borderRadius: 2,
                height: '100%',
                padding: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                boxShadow:
                  ' rgba(6, 24, 44, 0.4) 0px 0px 0px 2px, rgba(6, 24, 44, 0.65) 0px 4px 6px -1px, rgba(255, 255, 255, 0.08) 0px 1px 0px inset;',
                backgroundColor: getBackgroundColor(data?.jmV2ExtendedData?.next1Status)?.jobMonitorBgColor,
                '&:hover': {
                  background: getBackgroundColor(data?.jmV2ExtendedData?.next1Status)?.jobMonitorHoverColor,
                },
              }}
            >
              <Typography fontWeight="bold">{data.jmV2ExtendedData?.next1Label}</Typography>
              <Typography fontWeight="bold" fontStyle="italic">
                {data.jmV2ExtendedData?.next1OperationName}
              </Typography>
              <Typography>{data.jmV2ExtendedData?.nextStatus1Label}</Typography>
            </Box>
          </Grid>
          <Grid item xs={1.5}>
            <Box
              sx={{
                borderRadius: 2,
                height: '100%',
                padding: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                boxShadow:
                  ' rgba(6, 24, 44, 0.4) 0px 0px 0px 2px, rgba(6, 24, 44, 0.65) 0px 4px 6px -1px, rgba(255, 255, 255, 0.08) 0px 1px 0px inset;',
                backgroundColor: getBackgroundColor(data?.jmV2ExtendedData?.next2Status)?.jobMonitorBgColor,
                '&:hover': {
                  background: getBackgroundColor(data?.jmV2ExtendedData?.next2Status)?.jobMonitorHoverColor,
                },
              }}
            >
              <Typography fontWeight="bold">{data.jmV2ExtendedData?.next2Label}</Typography>
              <Typography fontWeight="bold" fontStyle="italic">
                {data.jmV2ExtendedData?.next2OperationName}
              </Typography>
              <Typography>{data.jmV2ExtendedData?.nextStatus2Label}</Typography>
            </Box>
          </Grid>
          <ConfirmDialogUI
            message={confirmDialogData?.message}
            confirmButtonText={confirmDialogData?.confirmButtonText}
            disabled={clicked}
            joker1ButtonText={confirmDialogData?.joker1ButtonText}
            joker2ButtonText={confirmDialogData?.joker2ButtonText}
            handleChangeElement={confirmDialogData?.handleChangeElement}
            handleChangeJoker1Element={confirmDialogData?.handleChangeJoker1Element}
            handleChangeJoker2Element={confirmDialogData?.handleChangeJoker2Element}
            show={showConfirmDialog}
            onHide={() => setShowConfirmDialog(false)}
            largeButtons={true}
            maxWidth="md"
          />
          <OpWidgetReasonDialog
            open={isInterruptionDialogOpen}
            handleClose={() => setIsInterruptionDialogOpen(false)}
            data={reasons.interruption}
            setSelectedReasonCode={setSelectedReasonCode}
            setIsQuantityDialogOpen={setIsQuantityDialogOpen}
            setShowConfirmDialog={setShowConfirmDialog}
            handleInterruptOperation={handleInterruptOperation}
            isGrouped={isGrouped}
            mode="interrupt"
            setIsGrouppedDialogOpen={setIsGrouppedDialogOpen}
          />
          <OpWidgetReasonDialog
            open={isDowntimeDialogOpen}
            handleClose={() => setIsDowntimeDialogOpen(false)}
            data={reasons.downtime}
            handleStopOperation={handleStopOperation}
            mode="downtime"
          />
          <OpWidgetMultiManDialog
            open={isEmployeeGroupDialogOpen}
            handleClose={() => setIsEmployeeDialogOpen(false)}
            data={employeeGroups}
            setSelectedEmployees={setSelectedEmployees}
            handleStartOperation={handleStartOperation}
          />
          <OpWidgetQuantityDialog
            open={isQuantityDialogOpen}
            handleClose={() => setIsQuantityDialogOpen(false)}
            handleFinishOperation={handleFinishOperation}
            handleInterruptOperation={handleInterruptOperation}
            mode={selectedReasonCode ? 'interrupt' : 'finish'}
            autoCompleteOptions={autoCompleteOptions}
          />
          <OpWidgetGrouppedQuantityDialog
            open={isGrouppedDialogOpen}
            handleClose={() => setIsGrouppedDialogOpen(false)}
            data={groupedOperations}
            handleFinishOperation={selectedReasonCode ? handleGroupedInterruptOperation : handleFinishOperation}
            autoCompleteOptions={autoCompleteOptions}
          />

          <AttachmentDialogUI
            open={isAttachmentDialogOpen}
            data={attachments}
            handleClose={() => setIsAttachmentDialogOpen(false)}
          />
        </Grid>
      </Box>
    </Grid>
  );
};

export default OpWidgetJobmonitorCard2;
